import React, { FC, MouseEvent, useCallback, useLayoutEffect, useState } from 'react';

import styles from './ripple.scss';

const DEFAULT_ANIMATION_DURATION = 600;

const useDebouncedRippleCleanUp = (
  rippleCount: number,
  duration: number,
  cleanUpFunction: () => void,
) => {
  useLayoutEffect(() => {
    let bounce = null;
    if (rippleCount > 0) {
      clearTimeout(bounce);

      bounce = setTimeout(() => {
        cleanUpFunction();
        clearTimeout(bounce);
      }, duration * 4);
    }

    return () => clearTimeout(bounce);
  }, [rippleCount, duration, cleanUpFunction]);
};

type Props = {
  dataId?: string;
  dataTestId?: string;
};

export const Ripple: FC<Props> = (props: Props) => {
  const [rippleArray, setRippleArray] = useState([]);

  useDebouncedRippleCleanUp(rippleArray.length, DEFAULT_ANIMATION_DURATION, () => {
    setRippleArray([]);
  });

  const addRipple = useCallback(
    (event: MouseEvent) => {
      const rippleContainer = event.currentTarget.getBoundingClientRect();

      const size =
        rippleContainer.width > rippleContainer.height
          ? rippleContainer.width
          : rippleContainer.height;
      const x = event.clientX - rippleContainer.x - size / 2;
      const y = event.clientY - rippleContainer.y - size / 2;
      const newRipple = {
        x,
        y,
        size,
      };

      setRippleArray([...rippleArray, newRipple]);
    },
    [rippleArray],
  );

  return (
    <div className={styles.wrapper} onClick={addRipple} {...props}>
      {rippleArray.length > 0 &&
        rippleArray.map((ripple, index) => (
          <span
            className={styles.wave}
            key={`wave-${ripple.x}-${ripple.y}-${index}`}
            style={{
              top: ripple.y,
              left: ripple.x,
              width: ripple.size,
              height: ripple.size,
              animationDuration: `${DEFAULT_ANIMATION_DURATION}ms`,
            }}
          />
        ))}
    </div>
  );
};

Ripple.displayName = 'Ripple';
